import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MoreVertical, Play, Clock, Settings, FileEdit, BarChart3, FolderInput, Copy, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Video } from '@/types/hierarchicalFolders';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { DeleteVideoModal } from '@/components/shared/DeleteVideoModal';
import { RenameVideoModal } from '@/components/shared/RenameVideoModal';
import { CopyCodeModal } from '@/components/shared/CopyCodeModal';
import { downloadVideoDirectLink } from '@/utils/downloadVideo';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';

interface VideoCardPremiumProps {
  video: Video;
  onClick?: () => void;
  onEdit?: () => void;
  onMove?: () => void;
  onDuplicate?: () => void;
  onDelete?: () => void;
  onViewStats?: () => void;
}

export function VideoCardPremium({
  video,
  onClick,
  onEdit,
  onMove,
  onDuplicate,
  onDelete,
  onViewStats,
}: VideoCardPremiumProps) {
  useLanguage();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isHovered, setIsHovered] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [copyCodeModalOpen, setCopyCodeModalOpen] = useState(false);

  const isVideoProcessing = video.status === 'processing';
  const [showProcessingOverlay, setShowProcessingOverlay] = useState(isVideoProcessing);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const prevProcessingRef = useRef(isVideoProcessing);

  useEffect(() => {
    if (isVideoProcessing && !prevProcessingRef.current) {
      setShowProcessingOverlay(true);
      setIsAnimatingOut(false);
    } else if (!isVideoProcessing && prevProcessingRef.current) {
      setIsAnimatingOut(true);
      const timer = setTimeout(() => {
        setShowProcessingOverlay(false);
        setIsAnimatingOut(false);
      }, 400);
      return () => clearTimeout(timer);
    } else if (isVideoProcessing) {
      setShowProcessingOverlay(true);
    }
    prevProcessingRef.current = isVideoProcessing;
  }, [isVideoProcessing]);

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const statusConfig = {
    processing: {
      label: translateInterfaceText("Processando"),
      className: 'bg-[#007BFF] text-white',
      animate: true,
    },
    active: {
      label: translateInterfaceText("Ativo"),
      className: 'bg-[#10B981] text-white',
      animate: false,
    },
    error: {
      label: translateInterfaceText("Erro"),
      className: 'bg-[#EF4444] text-white',
      animate: false,
    },
  };

  const status = statusConfig[video.status] || statusConfig.active;

  return (
    <div
      className={cn(
        "group relative bg-white rounded-[14px] overflow-hidden",
        "transition-all duration-200 ease-[cubic-bezier(.25,.8,.25,1)]",
        "border border-[#E5E7EB]",
        "hover:scale-[1.02] hover:-translate-y-0.5",
        "hover:shadow-[0_8px_24px_rgba(123,97,255,0.15)]",
        "hover:border-[#DCE1FF]",
        isVideoProcessing ? "cursor-not-allowed" : "cursor-pointer"
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={isVideoProcessing ? undefined : onClick}
    >
      {/* Thumbnail Container */}
      <div className="relative w-full aspect-video bg-[#F3F4F6]">
        {video.thumbnailUrl ? (
          <img
            src={video.thumbnailUrl}
            alt={video.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Play className="h-12 w-12 text-[#9CA3AF]" />
          </div>
        )}

        {/* Processing Overlay */}
        {showProcessingOverlay && (
          <div
            className={cn(
              "absolute inset-0 z-20 flex flex-col items-center justify-center",
              "transition-opacity duration-[400ms] ease-out",
              "backdrop-blur-[3px]",
              isAnimatingOut ? "opacity-0" : "opacity-100"
            )}
            style={{ background: 'rgba(26, 26, 26, 0.65)' }}
          >
            <div className="w-[38px] h-[38px] border-[3px] border-white/35 border-t-white rounded-full animate-spin mb-3" />
            <span className="text-white/90 text-[15px] font-semibold tracking-[0.2px]">
              {translateInterfaceText("Processando…")}</span>
            <span className="text-white/75 text-[14px] mt-1">
              {translateInterfaceText("Seu vídeo está sendo processado…")}</span>
          </div>
        )}

        {/* Duration Badge - Bottom Right */}
        <div className="absolute bottom-3 right-3 px-2 py-0.5 rounded bg-black/80 text-white text-xs font-medium flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {formatDuration(video.duration)}
        </div>

        {/* Hover Overlay */}
        {isHovered && (
          <div className="absolute inset-0 bg-black/5 transition-opacity duration-200" />
        )}
      </div>

      {/* Content */}
      <div className="p-3">
        <h3 className="font-semibold text-sm text-[#111827] truncate mb-2">
          {video.title}
        </h3>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 text-xs text-[#6B7280]">
            <span className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              {formatDuration(video.duration)}
            </span>
            <span className="text-[#D1D5DB]">•</span>
            <div 
              className="px-2.5 py-1 rounded-lg text-xs font-semibold whitespace-nowrap inline-block"
              style={{
                backgroundColor: 'rgba(65, 105, 225, 0.08)',
                color: '#4169E1'
              }}
            >
              {video.plays?.toLocaleString(getInterfaceLanguage()) || 0}
            </div>
          </div>

          {/* Actions Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-[#6B7280] opacity-100 hover:bg-transparent"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={(e) => { 
                e.stopPropagation(); 
                navigate(`/personalizations/${video.id}`); 
              }}>
                <Settings className="h-4 w-4 mr-2" />
                {translateInterfaceText("Personalizar")}</DropdownMenuItem>
              
              <DropdownMenuItem onClick={(e) => { 
                e.stopPropagation(); 
                setRenameModalOpen(true);
              }}>
                <FileEdit className="h-4 w-4 mr-2" />
                {translateInterfaceText("Renomear")}</DropdownMenuItem>
              
              {onViewStats && (
                <DropdownMenuItem onClick={(e) => { 
                  e.stopPropagation(); 
                  navigate(`/metrics?videoId=${video.id}`);
                }}>
                  <BarChart3 className="h-4 w-4 mr-2" />
                  {translateInterfaceText("Ver Métricas")}</DropdownMenuItem>
              )}
              
              <DropdownMenuSeparator />
              
              <DropdownMenuItem onClick={(e) => { 
                e.stopPropagation(); 
                setCopyCodeModalOpen(true);
              }}>
                <Copy className="h-4 w-4 mr-2" />
                {translateInterfaceText("Copiar código")}</DropdownMenuItem>
              
              {onMove && (
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onMove(); }}>
                  <FolderInput className="h-4 w-4 mr-2" />
                  {translateInterfaceText("Mover para outra pasta")}</DropdownMenuItem>
              )}
              
              {onDuplicate && (
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDuplicate(); }}>
                  <Copy className="h-4 w-4 mr-2" />
                  {translateInterfaceText("Duplicar")}</DropdownMenuItem>
              )}
              
              <DropdownMenuSeparator />
              
              {onEdit && (
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(); }}>
                  {translateInterfaceText("Editar vídeo")}</DropdownMenuItem>
              )}
              
              {onDelete && (
                <DropdownMenuItem
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    setDeleteModalOpen(true);
                  }}
                  className="text-[#EF4444] data-[highlighted]:bg-red-600 data-[highlighted]:text-white"
                >
                  {translateInterfaceText("Excluir vídeo")}</DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      
      {/* Modal de confirmação de exclusão */}
      {onDelete && (
        <DeleteVideoModal
          open={deleteModalOpen}
          onOpenChange={setDeleteModalOpen}
          videoTitle={video.title}
          onConfirm={async () => {
            onDelete();
          }}
        />
      )}
      
      {/* Modal de renomear */}
      <RenameVideoModal
        open={renameModalOpen}
        onOpenChange={setRenameModalOpen}
        currentTitle={video.title}
        onConfirm={async (newTitle) => {
          // Atualizar título do vídeo
          toast.success(translateInterfaceText("Vídeo renomeado com sucesso"));
        }}
      />
      
      {/* Modal de copiar código */}
      <CopyCodeModal
        isOpen={copyCodeModalOpen}
        onClose={() => setCopyCodeModalOpen(false)}
        entityType="video"
        entityId={video.id}
        entityName={video.title}
        userId={user?.id_user}
        videoOrigin="ORIGINAL"
        hideJsTab={false}
      />
    </div>
  );
}
